import { motion } from 'framer-motion'
import SectionTitle from '../ui/SectionTitle'

const FEATURES = [
  {
    icon: '✅',
    title: 'Stress-Free Planning',
    desc:  "We handle everything so you can simply enjoy watching your child's happiness.",
  },
  {
    icon: '✨',
    title: 'Full Event Coordination',
    desc:  'From setup to cleanup — decoration, entertainment, catering — we cover it all.',
  },
  {
    icon: '🛡️',
    title: 'Safe & Professional Team',
    desc:  'Trained staff with experience working with children in a fun, safe environment.',
  },
  {
    icon: '🎨',
    title: 'Creative Experiences',
    desc:  'Unique themes and magical touches — no two parties look the same.',
  },
]

const containerVariants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.12 } },
}

const itemVariants = {
  hidden: { opacity: 0, x: 30 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.55, ease: 'easeOut' } },
}

export default function WhyUs() {
  return (
    <section className="section-padding bg-surface overflow-hidden">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* ── Left: illustration with balloon decorations ─────────────── */}
          <motion.div
            className="relative flex justify-center"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* Decorative background circle */}
            <div className="absolute inset-8 rounded-full bg-gradient-card opacity-80 blur-sm" />

            {/* Main image */}
            <div className="relative z-10 w-full max-w-sm aspect-square">
              <img
                src="/assets/mouse-kids.png"
                alt="Farafeero team with children and balloons"
                className="w-full h-full object-contain drop-shadow-xl"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  e.currentTarget.nextElementSibling.style.display = 'flex'
                }}
              />
              {/* Fallback */}
              <div
                className="hidden w-full h-full rounded-3xl bg-gradient-card items-center justify-center"
                aria-hidden="true"
              >
                <div className="text-center">
                  <p className="text-8xl mb-4">🐭🎈</p>
                  <p className="font-display text-ink text-xl">Making Magic</p>
                </div>
              </div>
            </div>

            {/* Floating balloon decorations around image */}
            {[
              { emoji: '🎈', top: '5%',   right: '8%',  delay: '0s',   cls: 'text-4xl' },
              { emoji: '🎊', top: '30%',  left: '2%',   delay: '0.5s', cls: 'text-3xl' },
              { emoji: '⭐', bottom:'15%', right: '3%',  delay: '1s',   cls: 'text-3xl' },
              { emoji: '🎀', bottom:'5%',  left: '10%',  delay: '1.5s', cls: 'text-4xl' },
            ].map(({ emoji, delay, cls, ...pos }) => (
              <span
                key={emoji}
                className={`balloon absolute pointer-events-none select-none ${cls}`}
                style={{ ...pos, animationDelay: delay }}
                aria-hidden="true"
              >
                {emoji}
              </span>
            ))}

            {/* Stats chip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-glow-primary px-6 py-3 flex items-center gap-4 whitespace-nowrap z-20"
            >
              <div className="text-center">
                <p className="font-display text-primary text-xl leading-none">500+</p>
                <p className="font-body text-ink-muted text-xs">Parties</p>
              </div>
              <div className="w-px h-8 bg-primary/20" />
              <div className="text-center">
                <p className="font-display text-primary text-xl leading-none">100%</p>
                <p className="font-body text-ink-muted text-xs">Happy Clients</p>
              </div>
              <div className="w-px h-8 bg-primary/20" />
              <div className="text-center">
                <p className="font-display text-primary text-xl leading-none">10+</p>
                <p className="font-body text-ink-muted text-xs">Years</p>
              </div>
            </motion.div>
          </motion.div>

          {/* ── Right: title + feature list ──────────────────────────────── */}
          <div className="pt-8 lg:pt-0">
            <SectionTitle
              eyebrow="Why Us"
              title="Why Families Trust Farafeero"
              centered={false}
            />

            <motion.ul
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
              className="space-y-6"
            >
              {FEATURES.map(({ icon, title, desc }) => (
                <motion.li
                  key={title}
                  variants={itemVariants}
                  className="flex items-start gap-5 group"
                >
                  {/* Icon circle */}
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl shrink-0 transition-all duration-300 group-hover:bg-primary group-hover:scale-110">
                    {icon}
                  </div>

                  <div>
                    <h3 className="font-display text-ink text-lg mb-1">{title}</h3>
                    <p className="font-body text-ink-muted text-sm leading-relaxed">{desc}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-8"
            >
              <a
                href="tel:07775000"
                className="inline-flex items-center gap-2 font-body font-bold text-primary hover:text-primary-dark transition-colors group"
              >
                📞 Call us: 07775000
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
